import { DeepPartial, useForm } from "react-hook-form";
import { MailSendingApi } from "src/api/generated/common/mail/mailSendingApi";
import { useOAuthAuthentication } from "src/utils/oAuth.ts";
import { AavoForm } from "src/components/common/forms/AavoForm.tsx";
import i18n from "i18next";
import { FormCommonProps } from "../forms/types";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { emailRule, requireRule } from "src/components/common/forms/validation.ts";
import { FormRichTextEditor } from "src/components/common/forms/fields/FormRichTextEditor.tsx";

export interface SendEmailFormProps extends FormCommonProps<void> {
	defaultValues?: DeepPartial<SendEmailFormValues>;
}

export interface SendEmailFormValues {
	receiverEmail: string;
	subject: string;
	body: string;
}

export const SendEmailForm = ({ defaultValues, onCompleted, onFormEdited }: SendEmailFormProps) => {
	const {
		control,
		handleSubmit,
		watch,
		formState: { isDirty },
	} = useForm<SendEmailFormValues>({
		mode: "all",
		defaultValues: defaultValues ?? {},
	});

	if(onFormEdited)
		watch(onFormEdited)

	const { checkAuthentication } = useOAuthAuthentication(
		MailSendingApi.ensureMailSendingAuthentication,
	);

	const submitForm = async (values: SendEmailFormValues) => {
		const authResult = await checkAuthentication();
		if (authResult === "requiresAuthentication") {
			return "interrupted";
		}

		await MailSendingApi.sendMailAsCurrentUser({
			receiver: values.receiverEmail,
			subject: values.subject,
			body: values.body,
		});
	};

	return (
		<AavoForm
			handleSubmit={handleSubmit}
			isDirty={isDirty}
			submit={submitForm}
			submitLabel={i18n.t("send")}
			fitContent={true}
			columns={1}
			onCompleted={onCompleted}
		>
			<FormTextField
				control={control}
				name={"receiverEmail"}
				rules={{
					...requireRule(),
					...emailRule(),
				}}
				tooltip={i18n.t("you_can_use_semicolon_to_separate_multiple_email_addresses")}
				label={i18n.t("receiver_mail")}
			/>
			<FormTextField
				control={control}
				name={"subject"}
				rules={requireRule()}
				label={i18n.t("title")}
			/>
			<FormRichTextEditor
				control={control}
				name={"body"}
				sx={{
					flex: 1,
					gridColumn: "1 / -1",
				}}
			/>
		</AavoForm>
	);
};
